import React, { useEffect, useState } from 'react';
import { Plus, QrCode, Trash, Power, ArrowsClockwise, Phone, Pulse } from '@phosphor-icons/react';
import {
  PageHeader,
  Surface,
  Button,
  Input,
  Label,
  Badge,
  Modal,
  EmptyState,
  Spinner,
  useToast,
} from '@/components/ui-kit';
import { api } from '@/lib/api';
import { useInstances } from '@/context/InstanceContext';

export default function InstancesPage() {
  const { instances, refresh, loading } = useInstances();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newProvider, setNewProvider] = useState('evolution_go');
  const [qrFor, setQrFor] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const { show, node: toastNode } = useToast();

  const submit = async () => {
    if (!newName.trim()) return;
    try {
      setBusyId('__creating__');
      const res = await api.createInstance(newName.trim(), newProvider);
      show(`Instância "${newName}" criada`, 'success');
      setCreating(false);
      setNewName('');
      await refresh();
      // open QR right away
      openQr(newName.trim(), res?.data);
    } catch (e) {
      show(`Erro: ${e?.response?.data?.detail?.response?.message || e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const openQr = async (name, initialData = null) => {
    setQrFor(name);
    setQrData(initialData);
    setQrLoading(true);
    try {
      const d = await api.qrcode(name);
      setQrData(d);
    } catch (e) {
      show(`Erro QR: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setQrLoading(false);
    }
  };

  const closeQr = async () => {
    setQrFor(null);
    setQrData(null);
    await refresh();
  };

  const logout = async (name) => {
    if (!window.confirm(`Desconectar "${name}"?`)) return;
    setBusyId(name);
    try {
      await api.logoutInstance(name);
      show(`Instância "${name}" desconectada`, 'success');
      await refresh();
    } catch (e) {
      show(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const deleteInst = async (name) => {
    if (!window.confirm(`Excluir definitivamente "${name}"? Esta ação não pode ser desfeita.`))
      return;
    setBusyId(name);
    try {
      await api.deleteInstance(name);
      show(`Instância "${name}" removida`, 'success');
      await refresh();
    } catch (e) {
      show(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  // Poll QR every 5s while modal open
  useEffect(() => {
    if (!qrFor) return;
    const t = setInterval(async () => {
      try {
        const state = await api.state(qrFor);
        if (state?.instance?.state === 'open') {
          show('Conectada com sucesso!', 'success');
          closeQr();
        } else {
          // refresh QR (regenerated)
          const d = await api.qrcode(qrFor);
          setQrData(d);
        }
      } catch (e) {
        // silent
      }
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [qrFor]);

  return (
    <div data-testid="page-instances">
      <PageHeader
        badge="// Conexões WhatsApp"
        title="Instâncias"
        subtitle="Cada instância é um número WhatsApp conectado via Evolution API. Gerencie criação, conexão e desconexão."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={refresh}
              disabled={loading}
              data-testid="refresh-instances-btn"
            >
              <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
              Atualizar
            </Button>
            <Button onClick={() => setCreating(true)} data-testid="open-create-instance-btn">
              <Plus size={14} weight="bold" />
              Nova instância
            </Button>
          </>
        }
      />

      {loading && instances.length === 0 ? (
        <div className="text-zinc-400 flex items-center gap-2"><Spinner /> carregando…</div>
      ) : instances.length === 0 ? (
        <EmptyState
          icon={Pulse}
          title="Nenhuma instância criada ainda"
          hint="Crie sua primeira instância para escanear o QR Code e conectar um WhatsApp."
          action={<Button onClick={() => setCreating(true)} data-testid="empty-create-instance-btn">Criar agora</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {instances.map((inst) => (
            <InstanceCard
              key={inst.instanceName}
              inst={inst}
              onConnect={() => openQr(inst.instanceName)}
              onLogout={() => logout(inst.instanceName)}
              onDelete={() => deleteInst(inst.instanceName)}
              busy={busyId === inst.instanceName}
            />
          ))}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Nova instância"
        testId="create-instance-modal"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)} data-testid="cancel-create-instance">
              Cancelar
            </Button>
            <Button onClick={submit} disabled={!newName.trim() || busyId === '__creating__'} data-testid="confirm-create-instance">
              {busyId === '__creating__' ? <Spinner /> : 'Criar e gerar QR'}
            </Button>
          </>
        }
      >
        <Label>Nome da instância</Label>
        <Input
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="ex: vendas-01"
          data-testid="new-instance-name-input"
        />
        <Label className="mt-4">Provedor</Label>
        <select
          value={newProvider}
          onChange={(e) => setNewProvider(e.target.value)}
          className="w-full h-10 rounded-md border border-zinc-700 bg-zinc-950 px-3 text-sm text-zinc-100 outline-none focus:border-lime-400"
          data-testid="new-instance-provider-select"
        >
          <option value="evolution_go">Evolution GO (recomendado)</option>
          <option value="evolution_api">Evolution API (legado)</option>
        </select>
        <p className="text-xs text-zinc-400 mt-2 font-mono">
          Use nomes curtos, sem espaços. Esta é a chave usada para todas as operações.
        </p>
      </Modal>

      <Modal
        open={!!qrFor}
        onClose={closeQr}
        title={`Conectar ${qrFor || ''}`}
        testId="qr-modal"
        footer={<Button variant="secondary" onClick={closeQr} data-testid="close-qr-btn">Fechar</Button>}
      >
        <div className="text-center">
          {qrLoading && !qrData ? (
            <div className="py-12 text-zinc-400 flex flex-col items-center gap-3">
              <Spinner size={28} />
              <p className="text-sm font-mono">gerando QR Code…</p>
            </div>
          ) : qrData?.base64 ? (
            <>
              <div className="bg-white p-3 inline-block rounded-sm">
                <img
                  src={qrData.base64.startsWith('data:') ? qrData.base64 : `data:image/png;base64,${qrData.base64}`}
                  alt="QR Code"
                  className="w-64 h-64 object-contain"
                  data-testid="qr-image"
                />
              </div>
              <p className="text-xs text-zinc-400 mt-4 font-mono">
                Abra WhatsApp → ⋮ → Dispositivos conectados → Conectar dispositivo
              </p>
              {qrData.pairingCode && (
                <p className="text-sm text-lime-400 mt-2 font-mono">
                  Código pareamento: {qrData.pairingCode}
                </p>
              )}
            </>
          ) : qrData?.code ? (
            <pre className="text-xs text-zinc-300 bg-zinc-900 p-3 break-all text-left whitespace-pre-wrap">
              {qrData.code}
            </pre>
          ) : (
            <p className="text-zinc-400 py-8 font-mono">Aguardando QR…</p>
          )}
        </div>
      </Modal>

      {toastNode}
    </div>
  );
}

function InstanceCard({ inst, onConnect, onLogout, onDelete, busy }) {
  const status = inst.connectionStatus || 'unknown';
  const isOpen = status === 'open';
  const variant = isOpen ? 'success' : status === 'connecting' ? 'warn' : 'default';

  return (
    <Surface
      className="p-5 hover:border-zinc-700 transition-colors duration-200"
      data-testid={`instance-card-${inst.instanceName}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display font-bold text-lg text-zinc-50">{inst.instanceName}</h3>
          {inst.profileName && (
            <p className="text-xs text-zinc-400 mt-1 font-mono">{inst.profileName}</p>
          )}
        </div>
        <Badge variant={variant}>{status}</Badge>
      </div>

      {inst.ownerJid && (
        <div className="mt-3 flex items-center gap-2 text-xs font-mono text-zinc-400">
          <Phone size={12} />
          <span>{inst.ownerJid.replace('@s.whatsapp.net', '')}</span>
        </div>
      )}

      <div className="mt-5 flex gap-2 flex-wrap">
        {!isOpen ? (
          <Button size="sm" onClick={onConnect} disabled={busy} data-testid={`connect-${inst.instanceName}`}>
            <QrCode size={14} weight="bold" />
            Conectar
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={onLogout} disabled={busy} data-testid={`logout-${inst.instanceName}`}>
            <Power size={14} />
            Desconectar
          </Button>
        )}
        <Button size="sm" variant="danger" onClick={onDelete} disabled={busy} data-testid={`delete-${inst.instanceName}`}>
          <Trash size={14} />
          Excluir
        </Button>
      </div>
    </Surface>
  );
}
